import cx from 'classnames'
import { useTranslation } from 'react-i18next'

import bgHeaderPC from '../../assets/manual/bg-header-pc.png'
import bgHeader from '../../assets/manual/bg-header.png'
import imgLabelNotice from '../../assets/manual/label-notice.png'
import imgLabelWarning from '../../assets/manual/label-warning.png'
import imgPowerDiagram from '../../assets/manual/power-diagram.png'
import imgStep1WiFi from '../../assets/manual/step1-wifi.png'
import imgStep2QRPC from '../../assets/manual/step2-qr-pc.png'
import imgStep2QR from '../../assets/manual/step2-qr.png'
import imgStep3Photos from '../../assets/manual/step3-photos.png'
import imgStep4Sync from '../../assets/manual/step4-sync.png'



interface SeparatorProps {
  className?: string
}

const Separator = ({ className }: SeparatorProps) => {
  return (
    <div className={className}>
      <div className='bg-gray-300 h-[1px] w-full' />
    </div>
  )
}

interface LinkButtonProps {
  children: React.ReactNode
  className?: string
  to?: string
}

const LinkButton = ({ children, className, to = '#' }: LinkButtonProps) => {
  return (
    <a href={to} className={cx('text-on-surface-accent-primary font-semibold text-lg', className)}>
      {children}
    </a>
  )
}

interface SecondaryButtonProps {
  children: React.ReactNode
  className?: string
  to?: string
}

const SecondaryButton = ({ children, className, to = '#' }: SecondaryButtonProps) => {
  return (
    <a
      href={to}
      className={`border border-on-surface-accent-primary text-on-surface-accent-primary bg-surface-primary font-semibold px-4 py-2 rounded-lg ${className}`}
    >
      {children}
    </a>
  )
}

const HeroSectionContent = () => {
  const { t, i18n } = useTranslation('manual')
  const isJapanese = i18n.language === 'ja'

  return (
    <>
      <div className='text-center mb-8'>
        <h2 className='text-xl font-bold text-black mb-4 whitespace-pre-line'>
          {t('manual.hero.welcome')}
        </h2>
      </div>

      <div className='space-y-6'>
        <div
          className={cx('space-y-4 whitespace-pre-line', {
            'text-center': isJapanese,
          })}
        >
          {t('manual.hero.descriptionPC')}
        </div>

        <div className='flex justify-center'>
          <SecondaryButton to={t('manual.helpUrl')}>{t('manual.hero.helpCenter')}</SecondaryButton>
        </div>
      </div>
    </>
  )
}

const renderListItems = (text: string, className?: string) => {
  const items = text
    .split('\n')
    .map((line, index) => line.trim() && <li key={index}>{line}</li>)
    .filter(Boolean)

  return <ul className={className}>{items}</ul>
}

const ManualPage = () => {
  const { t, i18n } = useTranslation('manual')
  const isJapanese = i18n.language === 'ja'

  return (
    <div className='min-h-screen bg-white'>
      {/* Header */}
      <div className='bg-gray-50 relative w-full border-b border-gray-300'>
        <div className='pt-3 pb-2 flex items-center justify-center'>
          <h1 className='font-semibold text-gray-800'>{t('manual.headerTitle')}</h1>
        </div>

        {/* U.S. Acknowledgement */}
        {!isJapanese && (
          <div className='p-6 lg:max-w-5xl mx-auto'>
            You must read and understand this User Manual before using this Product. If you do not
            agree to the instructions and limitations in this User Manual, do not use the product
            and return it to the seller.
          </div>
        )}
      </div>

      <div className='max-w-md mx-auto lg:max-w-5xl'>
        {/* Hero Section */}
        <section
          className='lg:hidden px-4 py-16 lg:px-16 lg:py-24 bg-[#FBFBF6] bg-cover bg-center'
          style={{
            backgroundImage: `url(${bgHeader})`,
          }}
        >
          {HeroSectionContent()}
        </section>
        <section
          className='hidden lg:block px-4 py-16 lg:px-16 lg:py-24 bg-[#FBFBF6] bg-cover bg-center'
          style={{
            backgroundImage: `url(${bgHeaderPC})`,
          }}
        >
          {HeroSectionContent()}
        </section>

        {/* Table of Contents */}
        <section className='bg-white px-4 py-10 lg:px-16 lg:py-16'>
          <div className='space-y-2 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:space-y-0'>
            <div className='flex items-center justify-between'>
              <LinkButton to='#quickStart' className='p-2'>
                {t('manual.tableOfContents.quickStart')}
              </LinkButton>
            </div>
            <div className='flex items-center justify-between'>
              <LinkButton to='#basicOperation' className='p-2'>
                {t('manual.tableOfContents.basicOperation')}
              </LinkButton>
            </div>
            <div className='flex items-center justify-between'>
              <LinkButton to='#specifications' className='p-2'>
                {t('manual.tableOfContents.specifications')}
              </LinkButton>
            </div>
            <div className='flex items-center justify-between'>
              <LinkButton to='#legal' className='p-2'>
                {t('manual.tableOfContents.legal')}
              </LinkButton>
            </div>
            <div className='flex items-center justify-between'>
              <LinkButton to='#safety' className='p-2'>
                {t('manual.tableOfContents.safety')}
              </LinkButton>
            </div>
            <div className='flex items-center justify-between'>
              <LinkButton to='#warranty' className='p-2'>
                {t('manual.tableOfContents.warranty')}
              </LinkButton>
            </div>
          </div>
        </section>

        <Separator className='w-full' />

        {/* Section 1: Setup Guide */}
        <section id='quickStart' className='px-4 py-10 lg:px-16 lg:py-16'>
          <h2 className='text-2xl font-bold text-center text-gray-800 mb-6'>
            {t('manual.section1.title')}
          </h2>

          <div className='space-y-6'>
            <div className='space-y-4'>
              <p className='text-gray-800 leading-relaxed whitespace-pre-line'>
                {t('manual.section1.intro')}
              </p>
            </div>

            {/* Step 1 */}
            <div className='space-y-4 whitespace-pre-line'>
              <h3 className='text-lg font-semibold text-black'>
                {t('manual.section1.step1.title')}
              </h3>
              <div className='mt-2'>{t('manual.section1.step1.description')}</div>
              <div className='w-full h-[207px] rounded-lg overflow-hidden'>
                <img
                  alt='Step 1: Wi-Fi Setup'
                  className='h-full aspect-718/415 mx-auto'
                  src={imgStep1WiFi}
                />
              </div>
            </div>

            {/* Step 2 */}
            <div className='space-y-4 whitespace-pre-line'>
              <h3 className='text-lg font-semibold text-black'>
                {t('manual.section1.step2.title')}
              </h3>
              <div className='mt-2 space-y-3 pb-2'>{t('manual.section1.step2.description')}</div>
              <LinkButton to='https://family-album.onelink.me/cf5z/9m3v83wb' className='text-base'>
                {t('manual.section1.step2.downloadLink')}
              </LinkButton>
              <div className='w-full h-[207px] rounded-lg overflow-hidden'>
                <img
                  alt='Step 2: Account Setup'
                  className='h-full aspect-718/415 mx-auto'
                  src={imgStep2QR}
                />
              </div>
              <div className='hidden lg:block mt-2 w-full h-[207px] rounded-lg overflow-hidden'>
                <img
                  alt='Step 2: Account Setup'
                  className='h-full aspect-718/415 mx-auto'
                  src={imgStep2QRPC}
                />
              </div>
            </div>

            {/* Step 3 */}
            <div className='space-y-4 whitespace-pre-line'>
              <h3 className='text-lg font-semibold text-black'>
                {t('manual.section1.step3.title')}
              </h3>
              <div className='mt-2'>{t('manual.section1.step3.description')}</div>
              <div className='w-full h-[207px] rounded-lg overflow-hidden'>
                <img
                  alt='Step 3: Photo Selection'
                  className='h-full aspect-718/415 mx-auto'
                  src={imgStep3Photos}
                />
              </div>
            </div>

            {/* Step 4 */}
            <div className='space-y-4 whitespace-pre-line'>
              <h3 className='text-lg font-semibold text-black'>
                {t('manual.section1.step4.title')}
              </h3>
              <div className='mt-2'>{t('manual.section1.step4.description')}</div>
              <div className='w-full h-[207px] rounded-lg overflow-hidden'>
                <img
                  alt='Step 4: Syncing'
                  className='h-full aspect-718/415 mx-auto'
                  src={imgStep4Sync}
                />
              </div>
            </div>

            {/* Help Section */}
            <div className='space-y-3'>
              <h3 className='text-lg font-semibold text-black'>
                {t('manual.section1.contact.title')}
              </h3>
              <div className='space-y-2'>
                <div>
                  {t('manual.section1.contact.contactLabel')}
                  <br />
                  {t('manual.supportEmail')}
                </div>
                <LinkButton to={t('manual.helpUrl')} className='text-base block text-left'>
                  {t('manual.section1.contact.helpCenter')}
                </LinkButton>
              </div>
            </div>
          </div>
        </section>

        <Separator className='w-full' />

        {/* Section 2: Basic Operation */}
        <section id='basicOperation' className='px-4 py-10 lg:px-16 lg:py-16'>
          <h2 className='text-2xl font-bold text-center text-gray-800 mb-6'>
            {t('manual.section2.title')}
          </h2>

          <div className='space-y-8'>
            {/* Overview */}
            <div className='space-y-4'>
              {isJapanese && (
                <h3 className='text-xl font-bold text-black'>
                  {t('manual.section2.overview.title')}
                </h3>
              )}
              <div className='whitespace-pre-line'>{t('manual.section2.overview.description')}</div>
            </div>

            {/* Power & On/Off */}
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>{t('manual.section2.powerOn.title')}</h3>
              {renderListItems(t('manual.section2.powerOn.description'), 'list-disc ml-4')}
              {!isJapanese && <div>{t('manual.section2.powerOn.diagram')}</div>}
              <div className='w-full h-[214px] rounded-lg overflow-hidden'>
                <img
                  alt='Power diagram showing ports A and B'
                  className='h-full aspect-718/428 mx-auto'
                  src={imgPowerDiagram}
                />
              </div>
            </div>

            {/* Turn Frame Off */}
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>
                {t('manual.section2.powerOff.title')}
              </h3>
              {renderListItems(t('manual.section2.powerOff.description'), 'list-disc ml-4')}
              {isJapanese && (
                <div>
                  ⚠️ ご注意：同梱されていないケーブルやアクセサリーは使用しないでください。
                  故障や保証対象外となる場合があります。
                </div>
              )}
              {!isJapanese && (
                <>
                  <div>
                    <img alt='Warning' className='h-[24px] aspect-192/48' src={imgLabelWarning} />
                    <p className='mt-2 text-gray-800 text-sm'>
                      Important: Do not insert any other cables or accessories into other ports, as
                      this can cause electrical short-circuits, overheating, or fire, resulting in
                      device damage or injury.
                    </p>
                  </div>

                  <div>
                    <img alt='Notice' className='h-[24px] aspect-192/48' src={imgLabelNotice} />
                    <p className='mt-2 text-gray-800 text-sm'>
                      Using anything not included in the box may damage the device and void your
                      warranty.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Using the Frame */}
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>
                {t('manual.section2.operation.title')}
              </h3>
              {renderListItems(t('manual.section2.operation.description'), 'list-disc ml-4')}
            </div>

            {/* Connect to Wi-Fi */}
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>{t('manual.section2.wifi.title')}</h3>
              {renderListItems(t('manual.section2.wifi.description'), 'list-disc ml-4')}

              <div>
                <div className='flex gap-2'>
                  {!isJapanese && (
                    <img alt='Notice' className='h-[24px] aspect-192/48' src={imgLabelNotice} />
                  )}
                  {t('manual.section2.wifi.noteTitle')}
                </div>
                {renderListItems(t('manual.section2.wifi.note'), 'list-disc ml-4')}
              </div>
            </div>

            {/* Link to Account */}
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>{t('manual.section2.account.title')}</h3>
              {renderListItems(t('manual.section2.account.description'), 'list-disc ml-4')}
              <p className={cx(null, { 'font-bold text-black': !isJapanese })}>
                {t('manual.section2.account.option1Title')}
              </p>
              {renderListItems(t('manual.section2.account.option1'), 'list-disc ml-4')}
              <p className={cx(null, { 'font-bold text-black': !isJapanese })}>
                {t('manual.section2.account.option2Title')}
              </p>
              {renderListItems(t('manual.section2.account.option2'), 'list-disc ml-4')}
            </div>

            {!isJapanese && (
              <div className='space-y-4'>
                <h3 className='text-xl font-bold text-black'>Data &amp; Privacy Disclosure</h3>
                <div>
                  Use of this device requires a FamilyAlbum™ account and is subject to the
                  FamilyAlbum™ Terms of Use, available at https://family-album.com/en/terms.
                  <br />
                  When linked to the FamilyAlbum™ app, the product may process photos, metadata,
                  device and usage information to provide services. For details on what we collect,
                  how we use/share it, and your choices, please visit{' '}
                  <a
                    href='https://family-album.com/en/privacy_policy'
                    className='text-on-surface-accent-primary underline'
                  >
                    https://family-album.com/en/privacy_policy
                  </a>
                  .
                  <br />
                  Do not upload content you do not have rights to share. The services may change or
                  be discontinued in accordance with our Terms of Service.
                </div>
              </div>
            )}

            {/* Media Selection */}
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>
                {t('manual.section2.mediaSelection.title')}
              </h3>
              {renderListItems(t('manual.section2.mediaSelection.description'), 'list-disc ml-4')}
              {!isJapanese && <div>{t('manual.section2.mediaSelection.note')}</div>}
            </div>

            {/* Display Settings */}
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>
                {t('manual.section2.displaySettings.title')}
              </h3>
              {renderListItems(t('manual.section2.displaySettings.description'), 'list-disc ml-4')}
            </div>

            {/* Sleep Mode */}
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>{t('manual.section2.sleep.title')}</h3>
              {renderListItems(t('manual.section2.sleep.description'), 'list-disc ml-4')}
            </div>

            {/* Factory Reset */}
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>{t('manual.section2.reset.title')}</h3>
              <ul className='list-disc ml-4'>
                <li>
                  {t('manual.section2.reset.intro')}
                  {renderListItems(
                    t('manual.section2.reset.description'),
                    cx('ml-4', {
                      'list-disc': !isJapanese,
                      'list-[lower-alpha]': isJapanese,
                    }),
                  )}
                </li>
              </ul>
              <div>
                <div className='flex gap-2'>
                  {!isJapanese && (
                    <img alt='Notice' className='h-[24px] aspect-192/48' src={imgLabelNotice} />
                  )}
                  {t('manual.section2.reset.noteTitle')}
                </div>
                {renderListItems(t('manual.section2.reset.note'), 'list-disc ml-4')}
              </div>
            </div>
          </div>
        </section>

        <Separator className='w-full' />

        {/* Section 3: Product Specifications */}
        <section id='specifications' className='px-4 py-10 lg:px-16 lg:py-16'>
          <h2 className='text-2xl font-bold text-center text-gray-800 mb-6'>
            {t('manual.section3.title')}
          </h2>

          <div className='space-y-6'>
            <div>
              <h3 className='text-xl font-bold text-black mb-2'>
                {t('manual.section3.productName.title')}
              </h3>
              <p className='text-gray-800'>{t('manual.section3.productName.value')}</p>
            </div>

            {!isJapanese && (
              <div>
                <h3 className='text-xl font-bold text-black mb-2'>
                  {t('manual.section3.productModel.title')}
                </h3>
                <p className='text-gray-800'>{t('manual.section3.productModel.value')}</p>
              </div>
            )}

            <div>
              <h3 className='text-xl font-bold text-black mb-2'>
                {t('manual.section3.display.title')}
              </h3>
              <p className='text-gray-800'>{t('manual.section3.display.value')}</p>
            </div>

            <div>
              <h3 className='text-xl font-bold text-black mb-2'>
                {t('manual.section3.wifi.title')}
              </h3>
              {renderListItems(t('manual.section3.wifi.value'), 'text-gray-800 list-disc ml-4')}
            </div>

            <div>
              <h3 className='text-xl font-bold text-black mb-2'>
                {t('manual.section3.ports.title')}
              </h3>
              {renderListItems(t('manual.section3.ports.value'), 'text-gray-800 list-disc ml-4')}
            </div>

            <div>
              <h3 className='text-xl font-bold text-black mb-2'>
                {t('manual.section3.storage.title')}
              </h3>
              {renderListItems(t('manual.section3.storage.value'), 'text-gray-800 list-disc ml-4')}
            </div>
          </div>
        </section>

        <Separator className='w-full' />

        {/* Section 4: Legal & Compliance */}
        <section id='legal' className='px-4 py-10 lg:px-16 lg:py-16'>
          <h2 className='text-2xl font-bold text-center text-gray-800 mb-6'>
            {t('manual.section4.title')}
          </h2>

          <div className='space-y-8'>
            {!isJapanese && (
              <>
                <div className='space-y-2'>
                  <div className='text-xl font-bold'>FCC Compliance</div>
                  <div>
                    This device complies with Part 15 of the FCC Rules. Operation is subject to the
                    following two conditions:
                    <br />
                    (1) this device may not cause harmful interference, and
                    <br />
                    (2) this device must accept any interference received, including interference
                    that may cause undesired operation.
                  </div>
                  <img alt='Warning' className='h-[24px] aspect-192/48' src={imgLabelWarning} />
                  <div>
                    Changes or modifications not expressly approved by the party responsible for
                    compliance could void the user’s authority to operate the equipment.
                  </div>
                  <div>Interference Information – Class B Digital Device</div>
                  <div>
                    Note: This equipment has been tested and found to comply with the limits for a
                    Class B digital device, pursuant to Part 15 of the FCC Rules. These limits are
                    designed to provide reasonable protection against harmful interference in a
                    residential installation.
                  </div>
                  <div className='font-bold'>
                    Supplier Declaration of Conformity – Responsible Party (U.S.)
                  </div>
                  <div>
                    CONTAINS FCC ID: 2AN7T-MDP01
                    <br />
                    mixi America, Inc.
                    <br />
                    One World Trade Center Suite 8500, New York, NY 10007
                    <br />
                    global-support@family-album.com
                    <br />
                    This product complies with the applicable requirements of 47 CFR Part 15.
                  </div>
                  <div>
                    This equipment generates, uses and can radiate radio frequency energy and, if
                    not installed and used in accordance with the instructions, may cause harmful
                    interference to radio communications. However, there is no guarantee that
                    interference will not occur in a particular installation.
                    <br />
                    If this equipment does cause harmful interference to radio or television
                    reception, which can be determined by turning the equipment off and on, the user
                    is encouraged to try to correct the interference by one or more of the following
                    measures:
                  </div>
                  <ul className='list-disc ml-4'>
                    <li>Reorient or relocate the receiving antenna.</li>
                    <li>Increase the separation between the equipment and receiver.</li>
                    <li>
                      Connect the equipment into an outlet on a circuit different from that to which
                      the receiver is connected.
                    </li>
                    <li>Consult the dealer or an experienced radio/TV technician for help.</li>
                  </ul>
                </div>
                <div className='space-y-2'>
                  <div className='text-xl font-bold'>RF Exposure</div>
                  <div>
                    This equipment complies with FCC radiation exposure limits set forth for an
                    uncontrolled environment.
                    <br />
                    Install and operate this equipment with a minimum distance of 8 inches between
                    the radiator & your body.
                    <br />
                    This transmitter must not be co-located or operating in conjunction with any
                    other antenna or transmitter.
                  </div>
                </div>
              </>
            )}
            {isJapanese && (
              <>
                <div className='space-y-4'>
                  <h3 className='text-xl font-bold text-black'>
                    {t('manual.section4.radioLaw.title')}
                  </h3>
                  {renderListItems(t('manual.section4.radioLaw.description'), 'list-disc ml-4')}
                </div>

                <div className='space-y-4'>
                  <h3 className='text-xl font-bold text-black'>
                    {t('manual.section4.radioExposure.title')}
                  </h3>
                  {renderListItems(
                    t('manual.section4.radioExposure.description'),
                    'list-disc ml-4',
                  )}
                </div>
              </>
            )}
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>
                {t('manual.section4.intendedUse.title')}
              </h3>
              {renderListItems(t('manual.section4.intendedUse.description'), 'list-disc ml-4')}
            </div>
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>
                {t('manual.section4.environment.title')}
              </h3>
              {renderListItems(t('manual.section4.environment.description'), 'list-disc ml-4')}
            </div>
            <div className='space-y-4'>
              <h3 className='text-xl font-bold text-black'>
                {t('manual.section4.manufacturer.title')}
              </h3>
              {isJapanese ? (
                renderListItems(t('manual.section4.manufacturer.description'), 'list-disc ml-4')
              ) : (
                <div className='whitespace-pre-line'>
                  {t('manual.section4.manufacturer.description')}
                </div>
              )}
            </div>
          </div>
        </section>

        <Separator className='w-full' />

        {/* Section 5: Safety Information */}
        <section id='safety' className='px-4 py-10 lg:px-16 lg:py-16'>
          <h2 className='text-2xl font-bold text-center text-gray-800 mb-6'>
            {t('manual.section5.title')}
          </h2>

          <div className='space-y-6'>
            <div className='space-y-4'>
              {isJapanese ? (
                <div className='whitespace-pre-line'>
                  {t('manual.section5.overview.description')}
                </div>
              ) : (
                <div className='space-y-2'>
                  <p>
                    Please read and follow these safety instructions to ensure proper use of your
                    FamilyAlbum™ Digital Frame.
                  </p>
                  <img alt='Warning' className='h-[24px] aspect-192/48' src={imgLabelWarning} />
                  <p>
                    Failure to follow these safety instructions may result in electric shock, fire,
                    or damage to the device. Damage caused by misuse or unauthorized modifications
                    is not covered under warranty.
                  </p>
                </div>
              )}
            </div>

            <div className='mb-10'>
              <h3 className='text-xl font-bold text-black mb-4'>
                {t('manual.section5.childSafety.title')}
              </h3>
              {renderListItems(t('manual.section5.childSafety.description'), 'list-disc ml-4')}
              <div>{t('manual.section5.childSafety.note')}</div>
            </div>

            <div>
              {!isJapanese && (
                <img alt='Warning' className='h-[24px] aspect-192/48 mb-2' src={imgLabelWarning} />
              )}
              <h3 className='text-xl font-bold text-black mb-4'>
                {t('manual.section5.electricalSafety.title')}
              </h3>
              {renderListItems(t('manual.section5.electricalSafety.description'), 'list-disc ml-4')}
              {!isJapanese && (
                <div className='space-y-2 mt-2'>
                  <img alt='Notice' className='h-[24px] aspect-192/48' src={imgLabelNotice} />
                  <p>
                    This product does not contain a lithium battery and is powered only by the
                    external AC adapter.
                  </p>
                </div>
              )}
            </div>

            <div>
              {!isJapanese && (
                <img alt='Warning' className='h-[24px] aspect-192/48 mb-2' src={imgLabelWarning} />
              )}
              <h3 className='text-xl font-bold text-black mb-4'>
                {t('manual.section5.fireSafety.title')}
              </h3>
              {renderListItems(t('manual.section5.fireSafety.description'), 'list-disc ml-4')}
            </div>

            <div>
              {!isJapanese && (
                <img alt='Notice' className='h-[24px] aspect-192/48 mb-2' src={imgLabelNotice} />
              )}
              <h3 className='text-xl font-bold text-black mb-4'>
                {t('manual.section5.moistureSafety.title')}
              </h3>
              {renderListItems(t('manual.section5.moistureSafety.description'), 'list-disc ml-4')}
            </div>

            <div>
              {!isJapanese && (
                <img alt='Notice' className='h-[24px] aspect-192/48 mb-2' src={imgLabelNotice} />
              )}
              <h3 className='text-xl font-bold text-black mb-4'>
                {t('manual.section5.maintenance.title')}
              </h3>
              {renderListItems(t('manual.section5.maintenance.description'), 'list-disc ml-4')}
            </div>

            {!isJapanese && (
              <>
                <div>
                  <h3 className='text-xl font-bold text-black mb-4'>Wi-Fi Only</h3>
                  {renderListItems(
                    'IEEE 802.11 b/g/n (2.4 GHz only)\nBluetooth not supported',
                    'list-disc ml-4',
                  )}
                </div>

                <div>
                  <h3 className='text-xl font-bold text-black mb-4'>Connectivity & Ports</h3>
                  {renderListItems(
                    'USB-C port (Not available for use)\nSD card slot (Not available for use)\nDC power input',
                    'list-disc ml-4',
                  )}
                </div>

                <div>
                  <h3 className='text-xl font-bold text-black mb-4'>Storage & Media Support</h3>
                  {renderListItems(
                    '32 GB internal storage (approx. enough for ~25,000 photos)\nSupports standard photo, video, and audio formats',
                    'list-disc ml-4',
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        <Separator className='w-full' />

        {/* Section 6: Warranty Information */}
        <section id='warranty' className='px-4 py-10 lg:px-16 lg:py-16'>
          <h2 className='text-2xl font-bold text-center text-gray-800 mb-6'>
            {t('manual.section6.title')}
          </h2>

          <div className='space-y-6'>
            <div>
              <h3 className='text-xl font-bold text-black mb-4'>
                {t('manual.section6.warranty.title')}
              </h3>
              {renderListItems(
                t('manual.section6.warranty.description'),
                cx(null, { 'list-disc ml-4': isJapanese }),
              )}
            </div>

            {!isJapanese && (
              <>
                <div>
                  <p className='mb-2'>Disclaimer of Warranty</p>
                  <p>
                    EXCEPT AS EXPRESSLY PROVIDED ABOVE, THE PRODUCT AND SERVICES ARE PROVIDED “AS
                    IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
                    OTHER WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING ANY IMPLIED
                    WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
                    NON-INFRINGEMENT. Some states do not allow the exclusion of implied warranties,
                    and in that case, any implied warranty of merchantability or fitness for a
                    particular purpose on this product is limited in duration to the duration of
                    this warranty.
                  </p>
                </div>
                <div>
                  <h3 className='text-xl font-bold text-black mb-2'>Consumer Rights</h3>
                  <p>
                    This warranty gives you specific legal rights, and you may also have other
                    rights which vary from state to state.
                  </p>
                </div>
                <div>
                  <h3 className='text-xl font-bold text-black mb-2'>Limitation of Liability</h3>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR ANY INDIRECT,
                    INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS
                    OF DATA, LOSS OF USE, OR BUSINESS INTERRUPTION, ARISING OUT OF OR RELATED TO THE
                    PRODUCT OR THESE TERMS, EVEN IF ADVISED OF THE POSSIBILITY. IN NO EVENT WILL OUR
                    TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID FOR THE PRODUCT.
                    <br />
                    This limitation does not apply to liability that cannot be limited under
                    applicable law, including personal injury caused by our gross negligence or
                    willful misconduct. Some states do not allow the exclusion or limitation of
                    incidental or consequential damages, so the above may not apply to you.
                  </p>
                </div>
              </>
            )}

            <div>
              <h3 className='text-xl font-bold text-black mb-2'>
                {t('manual.section6.warrantyExclusions.title')}
              </h3>
              <ul className='list-disc ml-4'>
                <li>
                  {t('manual.section6.warrantyExclusions.intro')}
                  {renderListItems(
                    t('manual.section6.warrantyExclusions.exclusions'),
                    'list-disc ml-4',
                  )}
                </li>
                {isJapanese && <li>{t('manual.section6.warrantyExclusions.note')}</li>}
              </ul>
              {!isJapanese && (
                <div className='mt-10'>
                  <img alt='Notice' className='h-[24px] aspect-192/48 mb-2' src={imgLabelNotice} />
                  <div>{t('manual.section6.warrantyExclusions.note')}</div>
                </div>
              )}
            </div>

            {!isJapanese && (
              <div>
                <h3 className='text-xl font-bold text-black mb-2'>
                  Return Shipping & Instructions
                </h3>
                <p>
                  The customer is responsible for all costs of shipping the product back for
                  warranty service. Please back up any data, software, or other materials you may
                  have stored on your device. We will not be responsible for any loss of such
                  materials. Before returning any product, you must obtain shipping instructions by
                  contacting Contact Support at [global-support@family-album.com]. Returns sent
                  without following the provided instructions may be refused and will not be
                  processed.
                </p>
              </div>
            )}

            {isJapanese ? (
              <div className='mb-10'>
                <h3 className='text-xl font-bold text-black mb-4'>
                  {t('manual.section6.support.title')}
                </h3>
                {renderListItems(t('manual.section6.support.description'), 'list-disc ml-4')}
                <div className='mt-4 space-y-2'>
                  <div>
                    {t('manual.section6.support.contactLabel')}
                    <br />
                    {t('manual.supportEmail')}
                  </div>
                  <LinkButton to={t('manual.helpUrl')} className='p-0 text-base block text-left'>
                    {t('manual.section6.support.helpCenter')}
                  </LinkButton>
                </div>
              </div>
            ) : (
              <div className='space-y-3 mb-10'>
                <h3 className='text-lg font-semibold text-black'>
                  {t('manual.section1.contact.title')}
                </h3>
                <div className='space-y-2'>
                  Visit our{' '}
                  <a
                    href={t('manual.helpUrl')}
                    className='text-on-surface-accent-primary underline'
                  >
                    {t('manual.section6.support.helpCenter')}
                  </a>{' '}
                  or contact: global-support@family-album.com.
                  <br />
                  Our team is here to help with setup, troubleshooting, and warranty questions.
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default ManualPage
